import assertUnreachable from '@iconik-io/core/src/assert_unreachable';
import type { ExclusifyUnion, UnpackArray } from '@iconik-io/core/src/types';

import {
  type AssetsAssetCreateSchema,
  type AssetsAssetEditSchema,
  type AssetsCreateAssetVersionSchema,
  httpPatchAssetsByAssetId,
  httpPostAssets,
  httpPostAssetsByAssetIdVersions,
} from '../services/assets';
import type { IconikApiSdkMethodOptionsWithParams } from './types';

type AssetType = AssetsAssetCreateSchema['type'];

/** The ACL query flags the create endpoints accept. */
type AclQuery = {
  apply_default_acls?: boolean;
  apply_collection_acls?: boolean;
};

/**
 * Which ACLs a created asset or collection gets.
 * - 'default'     the workspace default ACLs
 * - 'collection'  the parent collection's ACLs
 * - 'private'     neither, so only the creator sees it
 * - 'unspecified' send no ACL flags and let the API decide (shares)
 */
export type ApplyAcls = 'default' | 'collection' | 'private' | 'unspecified';

function aclQuery(applyAcls: ApplyAcls): AclQuery {
  switch (applyAcls) {
    case 'default':
      return { apply_default_acls: true };
    case 'collection':
      return { apply_collection_acls: true };
    case 'private':
      // Both flags have to be sent explicitly, since omitting them lets the API
      // apply the default ACLs, which is what this mode asks it not to do.
      return { apply_default_acls: false, apply_collection_acls: false };
    case 'unspecified':
      return {};
    default:
      return assertUnreachable(applyAcls);
  }
}

type CreateAssetParams = {
  title: string;
  type?: AssetType;
  collectionId?: string;
  applyAcls?: ApplyAcls;
};

export function createAsset({
  client,
  params: { title, type = 'ASSET', collectionId, applyAcls = 'default' },
  abortSignal,
}: IconikApiSdkMethodOptionsWithParams<CreateAssetParams>) {
  return client.request(
    httpPostAssets(
      {
        collection_id: collectionId,
        title,
        type,
      },
      {
        assign_to_collection: collectionId !== undefined,
        ...aclQuery(applyAcls),
      },
    ),
    { abortSignal },
  );
}

type UpdateAssetParams = Pick<
  AssetsAssetEditSchema,
  'id' | 'title' | 'external_id' | 'type'
>;

export function updateAsset({
  client,
  params,
  abortSignal,
}: IconikApiSdkMethodOptionsWithParams<UpdateAssetParams>) {
  const { id, ...update } = params;
  return client.request(httpPatchAssetsByAssetId(id, update), { abortSignal });
}

export type CreateVersionCopyableSegment = UnpackArray<
  NonNullable<AssetsCreateAssetVersionSchema['include_segment_types']>
>;

export type CreateVersionParams = ExclusifyUnion<
  | {
      assetId: string;
    }
  | {
      assetId: string;
      copyMetadata: boolean;
      copySegments: CreateVersionCopyableSegment[];
      sourceVersionId: string;
    }
>;

export function createVersion({
  client,
  params,
  abortSignal,
}: IconikApiSdkMethodOptionsWithParams<CreateVersionParams>) {
  const { assetId } = params;
  const body: AssetsCreateAssetVersionSchema = {};

  if (params.copyMetadata !== undefined) {
    body.copy_metadata = params.copyMetadata;
    body.copy_segments = params.copySegments.length > 0;
    body.include_segment_types = params.copySegments;
    body.source_version_id = params.sourceVersionId;
  }
  return client.request(httpPostAssetsByAssetIdVersions(assetId, body), {
    abortSignal,
  });
}
